import React, { useEffect, useState } from "react"
import { graphql } from "gatsby"
import * as styles from "./luas-stop-times-module.module.scss"

const LUAS_FORECASTS_BASE_URL = "https://luasforecasts.rpa.ie/xml/get.ashx"
const LUAS_FALLBACK_STOPS_URL = "/luas-fallback/stops.json"
const LUAS_FALLBACK_FORECASTS_URL = "/luas-fallback/forecasts.json"

type LuasStop = {
  abrev: string
  name: string
}

type LuasLine = {
  name: string
  stops: LuasStop[]
}

type LuasTram = {
  dueMins: string
  destination: string
}

type LuasDirection = {
  name: string
  trams: LuasTram[]
}

type LuasForecast = {
  stop: string
  message: string
  directions: LuasDirection[]
}

type LuasFallbackStopsPayload = {
  generatedAt: string
  source: string
  lines: LuasLine[]
}

type LuasFallbackForecastsPayload = {
  generatedAt: string
  source: string
  failedStops: string[]
  forecasts: Record<string, LuasForecast>
}

type RenderProps = {
  label?: string | null
}

function parseStopsXml(xml: string): LuasLine[] {
  const doc = new DOMParser().parseFromString(xml, "application/xml")
  return Array.from(doc.querySelectorAll("line")).map(line => ({
    name: line.getAttribute("name") ?? "",
    stops: Array.from(line.querySelectorAll("stop")).map(stop => ({
      abrev: stop.getAttribute("abrev") ?? "",
      name: stop.textContent?.trim() ?? "",
    })),
  }))
}

function parseForecastXml(xml: string): LuasForecast {
  const doc = new DOMParser().parseFromString(xml, "application/xml")
  return {
    stop: doc.querySelector("stopInfo")?.getAttribute("stop") ?? "",
    message: doc.querySelector("message")?.textContent?.trim() ?? "",
    directions: Array.from(doc.querySelectorAll("direction")).map(dir => ({
      name: dir.getAttribute("name") ?? "",
      trams: Array.from(dir.querySelectorAll("tram")).map(tram => ({
        dueMins: tram.getAttribute("dueMins") ?? "",
        destination: tram.getAttribute("destination") ?? "",
      })),
    })),
  }
}

const LuasStopTimesModule: React.FC<RenderProps> = ({ label }) => {
  const [lines, setLines] = useState<LuasLine[]>([])
  const [selectedLine, setSelectedLine] = useState("")
  const [selectedStop, setSelectedStop] = useState("")
  const [forecast, setForecast] = useState<LuasForecast | null>(null)
  const [loading, setLoading] = useState(false)
  const [stopsError, setStopsError] = useState<string | null>(null)
  const [forecastError, setForecastError] = useState<string | null>(null)

  const fetchFallbackStops = async (): Promise<LuasLine[]> => {
    const fallbackResponse = await fetch(LUAS_FALLBACK_STOPS_URL)
    if (!fallbackResponse.ok) {
      throw new Error(`Fallback stops HTTP ${fallbackResponse.status}`)
    }

    const fallbackData: LuasFallbackStopsPayload = await fallbackResponse.json()
    return fallbackData.lines || []
  }

  const fetchFallbackForecast = async (stopAbrev: string): Promise<LuasForecast | null> => {
    const fallbackResponse = await fetch(LUAS_FALLBACK_FORECASTS_URL)
    if (!fallbackResponse.ok) {
      throw new Error(`Fallback forecasts HTTP ${fallbackResponse.status}`)
    }

    const fallbackData: LuasFallbackForecastsPayload = await fallbackResponse.json()
    return fallbackData.forecasts?.[stopAbrev.toUpperCase()] ?? null
  }

  useEffect(() => {
    fetch(`${LUAS_FORECASTS_BASE_URL}?action=stops&ver=2&encrypt=false`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then(xml => setLines(parseStopsXml(xml)))
      .catch(async error => {
        console.error("[luas] Live stops API unavailable, trying fallback", error)
        try {
          const fallbackLines = await fetchFallbackStops()
          if (fallbackLines.length > 0) {
            setLines(fallbackLines)
            return
          }
        } catch (fallbackError) {
          console.error("[luas] Stops fallback unavailable", fallbackError)
        }

        setStopsError("Could not load stops list. Please try again later.")
      })
  }, [])

  const stopsForLine = lines.find(line => line.name === selectedLine)?.stops ?? []

  const handleLineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLine(e.target.value)
    setSelectedStop("")
    setForecast(null)
    setForecastError(null)
  }

  const handleFind = () => {
    if (!selectedStop) return

    setLoading(true)
    setForecastError(null)
    setForecast(null)

    fetch(`${LUAS_FORECASTS_BASE_URL}?action=forecast&stop=${encodeURIComponent(selectedStop)}&ver=2&encrypt=false`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then(xml => setForecast(parseForecastXml(xml)))
      .catch(async error => {
        console.error("[luas] Live forecast API unavailable, trying fallback", error)
        try {
          const fallbackForecast = await fetchFallbackForecast(selectedStop)
          if (fallbackForecast) {
            setForecast(fallbackForecast)
            return
          }
        } catch (fallbackError) {
          console.error("[luas] Forecast fallback unavailable", fallbackError)
        }

        setForecastError("Could not load forecast. Please try again.")
      })
      .finally(() => setLoading(false))
  }

  return (
    <section className={styles.page}>
      <aside className={styles.sidebar}>
        <h2 className={styles.sidebarTitle}>{label || "Luas Stop Information"}</h2>

        {stopsError && <p className={styles.error}>{stopsError}</p>}

        <select className={styles.select} value={selectedLine} onChange={handleLineChange} aria-label="Select a line">
          <option value="">- Select a line -</option>
          {lines.map(line => (
            <option key={line.name} value={line.name}>
              {line.name}
            </option>
          ))}
        </select>

        <select
          className={styles.select}
          value={selectedStop}
          onChange={e => setSelectedStop(e.target.value)}
          disabled={!selectedLine}
          aria-label="Select a stop"
        >
          <option value="">- Select a stop -</option>
          {stopsForLine.map(stop => (
            <option key={stop.abrev} value={stop.abrev}>
              {stop.name}
            </option>
          ))}
        </select>

        <button className={styles.button} onClick={handleFind} disabled={!selectedStop || loading}>
          Find
        </button>
      </aside>

      <div className={styles.main}>
        {loading && <p className={styles.loading}>Loading forecasts...</p>}
        {forecastError && <p className={styles.error}>{forecastError}</p>}

        {forecast && (
          <>
            <h2 className={styles.stopName}>{forecast.stop}</h2>

            <div className={styles.directions}>
              {forecast.directions.map(direction => (
                <div key={direction.name} className={styles.directionBlock}>
                  <h3 className={styles.directionName}>{direction.name}</h3>
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Destination</th>
                          <th>Mins</th>
                        </tr>
                      </thead>
                      <tbody>
                        {direction.trams.length > 0 ? (
                          direction.trams.map((tram, index) => (
                            <tr key={`${tram.destination}-${tram.dueMins}-${index}`}>
                              <td>{tram.destination}</td>
                              <td>{tram.dueMins}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2}>No trams scheduled</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {forecast.message && <p className={styles.message}>{forecast.message}</p>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export const fragments = graphql`
  fragment LuasStopTimesModuleFields on DatoCmsLuasStopTimesModule {
    __typename
    id
    label
  }
`

export default LuasStopTimesModule
